/**
 * Shared bid cost inputs (§A). Labor is built from a crew of ROLE LINES — for
 * each role (Lead / Installer / Driver / Trainee) how many people AND how many
 * hours that role works — each priced at its configured `labor_rates` rate
 * (§A6f). labor = Σ(count × hours × rate); the blended rate (labor ÷ man-hours)
 * is derived for display. A "Flat amount" escape hatch covers odd jobs. Plus
 * round-trip mileage (× the configured rate) and a debris tier. Live total via
 * computeBid. Used by the project-detail Bid card AND the create form so the two
 * are identical. Controlled: the parent owns BidInputs.
 */
import { useEffect, useRef, useState } from 'react';
import { Map as MapIcon, Minus, Plus, X } from 'lucide-react';
import type { CostSettings, CrewRoleLines, LaborRates, MileageItinerary } from '@vari-crew/schemas';
import { computeBid, blendedLabor, EMPTY_CREW_ROLE_LINES } from '@vari-crew/schemas';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import {
  MileageCalculatorModal,
  type RoutingContext,
} from '@/components/ui/MileageCalculatorModal';
import { formatMoneyCents } from '@/lib/format';
import { cn } from '@/lib/cn';

export interface BidInputs {
  /** Resolved labor — the blended per-role calc (role mode) or a flat figure. */
  labor_cents: number | null;
  /** Per-role lines (count + hours each); null in flat mode. The derived crew,
   *  man-hours, and blended rate are computed server-side on save. */
  labor_roles: CrewRoleLines | null;
  mileage_miles: number | null;
  /** Saved multi-leg journey behind the miles (§A6g); null when entered manually. */
  mileage_legs: MileageItinerary | null;
  debris_quarters: number | null;
}

/** Prefill for a fresh calc — a representative crew (role lines) from the estimate. */
export interface LaborSeed {
  roles: CrewRoleLines;
}

type RoleKey = keyof CrewRoleLines;
type Counts = Record<RoleKey, number>;
type HoursStr = Record<RoleKey, string>;

const ROLE_ROWS: Array<{ key: RoleKey; label: string }> = [
  { key: 'lead', label: 'Lead' },
  { key: 'installer', label: 'Installer' },
  { key: 'driver', label: 'Driver' },
  { key: 'trainee', label: 'Trainee' },
];
const ROLE_KEYS = ROLE_ROWS.map((r) => r.key);

const DEFAULT_SETTINGS: CostSettings = {
  mileage_per_mile_cents: 85,
  debris_per_quarter_cents: 3000,
};
/** Used only until the real labor_rates load, so the UI can render. */
const FALLBACK_RATES: LaborRates = {
  lead: 7500,
  installer: 7500,
  driver: 7500,
  trainee: 7500,
  estimate_default: 7500,
};

const TIERS = [
  { q: 0, label: 'None' },
  { q: 1, label: '¼' },
  { q: 2, label: '½' },
  { q: 3, label: '¾' },
  { q: 4, label: 'Full' },
];

function dollars(cents: number | null | undefined): string {
  return cents != null ? (cents / 100).toFixed(2) : '';
}
function countsOf(l: CrewRoleLines): Counts {
  return {
    lead: l.lead.count,
    installer: l.installer.count,
    driver: l.driver.count,
    trainee: l.trainee.count,
  };
}
function hoursStrOf(l: CrewRoleLines): HoursStr {
  const f = (n: number) => (n > 0 ? String(n) : '');
  return {
    lead: f(l.lead.hours),
    installer: f(l.installer.hours),
    driver: f(l.driver.hours),
    trainee: f(l.trainee.hours),
  };
}
function buildLines(c: Counts, hs: HoursStr): CrewRoleLines {
  const h = (s: string) => (s.trim() ? Math.max(0, parseFloat(s) || 0) : 0);
  return {
    lead: { count: c.lead, hours: h(hs.lead) },
    installer: { count: c.installer, hours: h(hs.installer) },
    driver: { count: c.driver, hours: h(hs.driver) },
    trainee: { count: c.trainee, hours: h(hs.trainee) },
  };
}

export interface BidSummary {
  crew: number | null;
  manHours: number | null;
  laborCents: number;
  includesMileage: boolean;
  includesDebris: boolean;
  totalCents: number;
}

/**
 * Resolve a BidInputs into display/preview numbers — the per-role blended calc or
 * the flat figure, plus the computed grand total. Shared so the recipient preview
 * matches the editor exactly.
 */
export function summarizeBid(
  value: BidInputs,
  rates: LaborRates | null,
  settings: CostSettings | null,
): BidSummary {
  const r = rates ?? FALLBACK_RATES;
  const s = settings ?? DEFAULT_SETTINGS;
  let crew: number | null = null;
  let manHours: number | null = null;
  let laborCents: number;
  if (value.labor_roles) {
    const b = blendedLabor(value.labor_roles, r);
    crew = b.crew;
    manHours = b.man_hours;
    laborCents = b.labor_cents;
  } else {
    laborCents = value.labor_cents ?? 0;
  }
  const bid = computeBid(
    {
      labor_cents: laborCents,
      mileage_miles: value.mileage_miles ?? 0,
      debris_quarters: value.debris_quarters ?? 0,
    },
    s,
  );
  return {
    crew,
    manHours,
    laborCents,
    includesMileage: (value.mileage_miles ?? 0) > 0,
    includesDebris: (value.debris_quarters ?? 0) > 0,
    totalCents: bid.total_cents,
  };
}

/**
 * Collapse a task labor estimate into a representative crew: crew = weighted-avg
 * head count (person-hrs ÷ wall-clock-hrs); each member's hours = man-hrs ÷ crew
 * (so the seeded man-hours ≈ the estimate); mix = 1 lead + the rest installers (a
 * lone worker is an installer). The supervisor adjusts counts AND per-role hours.
 */
export function deriveLaborSeed(
  totalTaskMinutes: number,
  totalPersonHours: number,
): LaborSeed | null {
  const wallclock = totalTaskMinutes / 60;
  if (wallclock <= 0 || totalPersonHours <= 0) return null;
  const crew = Math.max(1, Math.round(totalPersonHours / wallclock));
  const hours = Math.round((totalPersonHours / crew) * 10) / 10;
  const roles: CrewRoleLines = {
    lead: { count: 0, hours: 0 },
    installer: { count: 0, hours: 0 },
    driver: { count: 0, hours: 0 },
    trainee: { count: 0, hours: 0 },
  };
  if (crew <= 1) roles.installer = { count: 1, hours };
  else {
    roles.lead = { count: 1, hours };
    roles.installer = { count: crew - 1, hours };
  }
  return { roles };
}

/**
 * Debris tier as a fillable truck. The cargo box is four separate bordered tiles
 * (¼ ½ ¾ Full) that fill cumulatively like a load gauge — click a tile to set the
 * volume; click the currently-filled level again to empty the truck (None). Hover
 * previews the resulting fill. The cab + wheels are decorative. The tiles reuse the
 * bid buttons' look: accent border + tint when filled, line border when empty. All
 * colors come from the app's CSS tokens, so light/dark mode just work.
 */
function DebrisTruck({
  quarters,
  perQuarterCents,
  onSelect,
}: {
  quarters: number;
  perQuarterCents: number;
  onSelect: (q: number) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  // Hovering a tile previews filling up to it; hovering the current level holds the
  // fill (clearing still happens on click — we just don't preview the empty truck).
  const effectiveQ = hover ?? quarters;

  const TILE_W = 34;
  const GAP = 3;
  const LEFT = 8;
  const TOP = 8;
  const TILE_H = 70;

  return (
    <svg
      viewBox="0 0 240 104"
      role="group"
      aria-label="Debris removal load"
      className="block w-full max-w-[240px] select-none"
    >
      {/* Wheels roll while the truck is hovered (spokes make the spin visible). */}
      <style>{`
        @keyframes debris-wheel-roll { to { transform: rotate(360deg); } }
        @keyframes debris-rumble {
          0%   { transform: translate(0, 0); }
          10%  { transform: translate(0.6px, -0.5px); }
          20%  { transform: translate(-0.5px, 0.4px); }
          30%  { transform: translate(0.4px, 0.6px); }
          40%  { transform: translate(-0.6px, -0.3px); }
          50%  { transform: translate(0.5px, 0.5px); }
          60%  { transform: translate(-0.4px, -0.6px); }
          70%  { transform: translate(0.6px, 0.2px); }
          80%  { transform: translate(-0.5px, 0.5px); }
          90%  { transform: translate(0.3px, -0.6px); }
          100% { transform: translate(0, 0); }
        }
        svg[aria-label="Debris removal load"]:hover .debris-cab {
          animation: debris-rumble 0.35s linear infinite;
        }
        svg[aria-label="Debris removal load"]:hover .debris-wheel {
          animation: debris-wheel-roll 0.8s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          svg[aria-label="Debris removal load"] .debris-cab,
          svg[aria-label="Debris removal load"] .debris-wheel { animation: none; }
        }
      `}</style>
      {/* chassis / bed — runs the full length of the box, under the cab */}
      <rect
        x={8}
        y={78}
        width={224}
        height={4}
        rx={2}
        style={{ fill: 'var(--color-line-strong)' }}
      />
      {/* wheels — rear under the box, front under the cab; spin on hover */}
      {[58, 205].map((cx) => (
        <g
          key={cx}
          className="debris-wheel"
          style={{ transformBox: 'fill-box', transformOrigin: 'center', pointerEvents: 'none' }}
        >
          <circle cx={cx} cy={90} r={11} style={{ fill: 'var(--color-ink-dim)' }} />
          {[0, 72, 144, 216, 288].map((a) => {
            const rad = (a * Math.PI) / 180;
            return (
              <line
                key={a}
                x1={cx}
                y1={90}
                x2={cx + 7.5 * Math.sin(rad)}
                y2={90 - 7.5 * Math.cos(rad)}
                style={{ stroke: 'var(--color-bg)', strokeWidth: 1.4, strokeLinecap: 'round' }}
              />
            );
          })}
          <circle cx={cx} cy={90} r={4.2} style={{ fill: 'var(--color-bg)' }} />
        </g>
      ))}
      {/* cab — faces right; rumbles (idles) only while the truck is hovered */}
      <g className="debris-cab">
        <path
          d="M153 28 L198 28 L214 49 L232 49 L232 78 L153 78 Z"
          style={{
            fill: 'var(--color-fill-soft)',
            stroke: 'var(--color-line-strong)',
            strokeWidth: 1.5,
            strokeLinejoin: 'round',
          }}
        />
        {/* front bumper */}
        <rect
          x={231}
          y={66}
          width={4}
          height={12}
          rx={1.5}
          style={{ fill: 'var(--color-line-strong)' }}
        />
        {/* windshield */}
        <path
          d="M197 31 L210 48 L214 48 L201 31 Z"
          style={{
            fill: 'var(--color-bg)',
            stroke: 'var(--color-line-strong)',
            strokeWidth: 1,
            strokeLinejoin: 'round',
          }}
        />
        {/* side window */}
        <rect
          x={160}
          y={34}
          width={28}
          height={14}
          rx={2}
          style={{ fill: 'var(--color-bg)', stroke: 'var(--color-line-strong)', strokeWidth: 1 }}
        />
        {/* door seam */}
        <line
          x1={178}
          y1={49}
          x2={178}
          y2={78}
          style={{ stroke: 'var(--color-line)', strokeWidth: 1 }}
        />
        {/* headlight */}
        <rect
          x={226}
          y={57}
          width={4}
          height={6}
          rx={1}
          style={{ fill: 'var(--color-bg)', stroke: 'var(--color-line-strong)', strokeWidth: 1 }}
        />
      </g>

      {/* cargo box — four separate tiles, cumulative fill */}
      {(['¼', '½', '¾', 'Full'] as const).map((label, idx) => {
        const i = idx + 1;
        const x = LEFT + idx * (TILE_W + GAP);
        const filled = i <= effectiveQ;
        const isCurrent = i === quarters;
        return (
          <g
            key={i}
            role="button"
            tabIndex={0}
            aria-label={
              isCurrent
                ? `Clear debris removal (currently ${label})`
                : `Set debris removal to ${label} — ${formatMoneyCents(i * perQuarterCents)}`
            }
            onClick={() => onSelect(isCurrent ? 0 : i)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(isCurrent ? 0 : i);
              }
            }}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={x}
              y={TOP}
              width={TILE_W}
              height={TILE_H}
              rx={3}
              style={{
                fill: filled
                  ? 'color-mix(in oklch, var(--color-accent) 14%, transparent)'
                  : 'transparent',
                stroke: filled ? 'var(--color-accent)' : 'var(--color-line)',
                strokeWidth: 1.5,
                transition: 'fill 120ms ease, stroke 120ms ease',
              }}
            />
            <text
              x={x + TILE_W / 2}
              y={TOP + TILE_H / 2}
              textAnchor="middle"
              dominantBaseline="central"
              style={{
                fill: filled ? 'var(--color-accent)' : 'var(--color-ink-dim)',
                fontSize: label === 'Full' ? 11 : 15,
                fontWeight: filled ? 600 : 500,
                transition: 'fill 120ms ease',
                pointerEvents: 'none',
              }}
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function BidBuilder({
  value,
  onChange,
  settings,
  rates = null,
  laborSeed = null,
  routing = null,
  lockLabor = false,
}: {
  value: BidInputs;
  onChange: (next: BidInputs) => void;
  settings?: CostSettings | null;
  /** Configured per-role labor rates ($/hr in cents). */
  rates?: LaborRates | null;
  /** Crew (role lines) to prefill the calc once, if nothing's entered/saved yet. */
  laborSeed?: LaborSeed | null;
  /** Project context for the mileage calculator; null hides the Calculate button. */
  routing?: RoutingContext | null;
  /** When true, Labor is READ-ONLY and derived from value.labor_roles (the crew
   *  set in "Plan the work"); editing is disabled. Mileage + debris stay editable. */
  lockLabor?: boolean;
}) {
  const s = settings ?? DEFAULT_SETTINGS;
  const r = rates ?? FALLBACK_RATES;
  const [calcOpen, setCalcOpen] = useState(false);

  const [mode, setMode] = useState<'role' | 'flat'>(() =>
    value.labor_roles != null ? 'role' : value.labor_cents != null ? 'flat' : 'role',
  );

  const initLines = value.labor_roles ?? laborSeed?.roles ?? EMPTY_CREW_ROLE_LINES;
  const [counts, setCounts] = useState<Counts>(() => countsOf(initLines));
  const [hoursStr, setHoursStr] = useState<HoursStr>(() => hoursStrOf(initLines));
  const [flatStr, setFlatStr] = useState(() => dollars(value.labor_cents));
  const [milesStr, setMilesStr] = useState(() =>
    value.mileage_miles != null ? String(value.mileage_miles) : '',
  );
  const quarters = value.debris_quarters ?? 0;

  const lines = buildLines(counts, hoursStr);
  const blended = blendedLabor(lines, r);
  const flatCents = flatStr.trim()
    ? Math.max(0, Math.round((parseFloat(flatStr) || 0) * 100))
    : null;
  const parsedMiles = milesStr.trim() ? parseFloat(milesStr) || 0 : null;
  const lockedBlended = lockLabor
    ? blendedLabor(value.labor_roles ?? EMPTY_CREW_ROLE_LINES, r)
    : null;
  const laborCents = lockLabor
    ? value.labor_cents ?? lockedBlended!.labor_cents
    : mode === 'role'
      ? blended.labor_cents
      : flatCents;

  // Prefill once from the estimate, committing it to `value` so an untouched-but-
  // prefilled labor still rides along on save. No value→input resync: the local
  // counts/hoursStr are the source of truth (resyncing would fight decimal typing
  // since labor_roles changes every keystroke).
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !laborSeed) return;
    if (value.labor_cents != null || value.labor_roles != null) return;
    seededRef.current = true;
    setCounts(countsOf(laborSeed.roles));
    setHoursStr(hoursStrOf(laborSeed.roles));
    onChange({
      ...value,
      labor_roles: laborSeed.roles,
      labor_cents: blendedLabor(laborSeed.roles, r).labor_cents,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborSeed]);

  useEffect(() => {
    if (value.mileage_miles != null && value.mileage_miles !== parsedMiles)
      setMilesStr(String(value.mileage_miles));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.mileage_miles]);

  const preview = computeBid(
    { labor_cents: laborCents ?? 0, mileage_miles: parsedMiles ?? 0, debris_quarters: quarters },
    s,
  );

  // --- emitters -------------------------------------------------------------
  function emitRole(c: Counts, hs: HoursStr) {
    const nextLines = buildLines(c, hs);
    onChange({
      ...value,
      labor_roles: nextLines,
      labor_cents: blendedLabor(nextLines, r).labor_cents,
    });
  }
  /** A sensible default hours for a role just added — match the busiest active role. */
  function defaultHours(): number {
    let m = 0;
    for (const k of ROLE_KEYS) {
      if (counts[k] > 0) {
        const h = parseFloat(hoursStr[k]) || 0;
        if (h > m) m = h;
      }
    }
    return m > 0 ? m : 8;
  }
  function setCount(key: RoleKey, n: number) {
    const clamped = Math.max(0, Math.min(99, Math.round(n) || 0));
    const nextCounts = { ...counts, [key]: clamped };
    let nextHours = hoursStr;
    if (clamped > 0 && !hoursStr[key].trim()) {
      nextHours = { ...hoursStr, [key]: String(defaultHours()) };
    }
    setCounts(nextCounts);
    if (nextHours !== hoursStr) setHoursStr(nextHours);
    emitRole(nextCounts, nextHours);
  }
  function setHours(key: RoleKey, str: string) {
    const next = { ...hoursStr, [key]: str };
    setHoursStr(next);
    emitRole(counts, next);
  }
  function setFlat(str: string) {
    setFlatStr(str);
    const cents = str.trim() ? Math.max(0, Math.round((parseFloat(str) || 0) * 100)) : null;
    onChange({ ...value, labor_cents: cents, labor_roles: null });
  }
  function switchMode(next: 'role' | 'flat') {
    if (next === mode) return;
    setMode(next);
    if (next === 'flat') {
      const cents =
        (blended.crew > 0 ? blended.labor_cents : null) ?? flatCents ?? value.labor_cents ?? null;
      setFlatStr(dollars(cents));
      onChange({ ...value, labor_cents: cents, labor_roles: null });
    } else {
      emitRole(counts, hoursStr);
    }
  }
  function setMiles(str: string) {
    setMilesStr(str);
    onChange({ ...value, mileage_miles: str.trim() ? parseFloat(str) || 0 : null });
  }
  function setQuarters(q: number) {
    onChange({ ...value, debris_quarters: q > 0 ? q : null });
  }

  return (
    <div className="space-y-4">
      {/* Larger screens: labor on the left; miles + debris stacked on the right. Mobile: one column. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-start">
        {/* Left column — Labor */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-ink-faint">Labor</label>
            {lockLabor ? (
              <span className="text-[11px] text-ink-faint">from Plan the work</span>
            ) : (
              <div className="flex rounded-md border border-line overflow-hidden text-[11px]">
                {(['role', 'flat'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => switchMode(m)}
                    className={cn(
                      'px-2.5 py-1 transition-colors',
                      mode === m
                        ? 'bg-accent/10 text-accent font-medium'
                        : 'text-ink-dim hover:bg-fill-soft',
                    )}
                  >
                    {m === 'role' ? 'By crew' : 'Flat amount'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {lockLabor ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-line">
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-3 py-1.5 text-[10px] uppercase tracking-wide text-ink-faint border-b border-line">
                  <span>Role</span>
                  <span className="text-center w-[60px]">Crew</span>
                  <span className="text-center w-[72px]">Hours each</span>
                  <span className="text-center w-[72px]">Total hours</span>
                </div>
                {ROLE_ROWS.filter(({ key }) => (value.labor_roles?.[key]?.count ?? 0) > 0).map(
                  ({ key, label }) => {
                    const line = value.labor_roles![key];
                    return (
                      <div
                        key={key}
                        className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center px-3 py-2 border-b border-line last:border-0"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{label}</div>
                          <div className="text-[11px] text-ink-faint">{formatMoneyCents(r[key])}/hr</div>
                        </div>
                        <div className="text-center w-[60px] text-sm tabular-nums">{line.count}</div>
                        <div className="text-center w-[72px] text-sm tabular-nums">{line.hours} h</div>
                        <div className="text-center w-[72px] text-sm font-medium tabular-nums">
                          {line.count * line.hours} h
                        </div>
                      </div>
                    );
                  },
                )}
                {!ROLE_ROWS.some(({ key }) => (value.labor_roles?.[key]?.count ?? 0) > 0) && (
                  <div className="px-3 py-3 text-[11px] text-ink-faint">
                    No crew set in Plan the work yet.
                  </div>
                )}
              </div>
              <div className="rounded-lg bg-fill-soft border border-line px-3 py-2 space-y-1">
                <div className="text-[11px] text-ink-faint">
                  {lockedBlended!.crew} crew · {lockedBlended!.man_hours} man-hours · blended{' '}
                  <span className="font-medium text-ink-dim">
                    {formatMoneyCents(lockedBlended!.blended_rate_cents)}/hr
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Labor</span>
                  <span className="text-sm font-semibold font-mono">{formatMoneyCents(laborCents ?? 0)}</span>
                </div>
              </div>
            </div>
          ) : mode === 'role' ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-line">
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-3 py-1.5 text-[10px] uppercase tracking-wide text-ink-faint border-b border-line">
                  <span>Role</span>
                  <span className="text-center w-[104px]">Crew</span>
                  <span className="text-center w-[72px]">Hours each</span>
                  <span className="text-center w-[64px]">Total hours</span>
                </div>
                {ROLE_ROWS.map(({ key, label }) => {
                  const count = counts[key] ?? 0;
                  const hrs = parseFloat(hoursStr[key]) || 0;
                  return (
                    <div
                      key={key}
                      className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center px-3 py-2 border-b border-line last:border-0"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{label}</div>
                        <div className="text-[11px] text-ink-faint">
                          {formatMoneyCents(r[key])}/hr
                        </div>
                      </div>
                      <div className="flex items-center gap-1 w-[104px] justify-center">
                        <button
                          type="button"
                          aria-label={`Remove ${label}`}
                          onClick={() => setCount(key, count - 1)}
                          disabled={count <= 0}
                          className="w-7 h-7 grid place-items-center rounded-md border border-line text-ink-dim hover:bg-fill-soft disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <input
                          type="number"
                          min={0}
                          max={99}
                          value={count === 0 ? '' : String(count)}
                          placeholder="0"
                          onChange={(e) => setCount(key, parseInt(e.target.value, 10) || 0)}
                          className="w-10 h-7 text-center text-sm rounded-md border border-line bg-transparent tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button
                          type="button"
                          aria-label={`Add ${label}`}
                          onClick={() => setCount(key, count + 1)}
                          className="w-7 h-7 grid place-items-center rounded-md border border-line text-ink-dim hover:bg-fill-soft"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="w-[72px] flex items-center gap-1 justify-center">
                        <input
                          type="number"
                          min={0}
                          step={0.5}
                          value={hoursStr[key]}
                          placeholder="0"
                          disabled={count <= 0}
                          onChange={(e) => setHours(key, e.target.value)}
                          className="w-12 h-7 text-center text-sm rounded-md border border-line bg-transparent tabular-nums disabled:opacity-40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-[11px] text-ink-faint">h</span>
                      </div>
                      <div className="w-[64px] text-center text-sm font-medium tabular-nums">
                        {count > 0 ? `${count * hrs} h` : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Blended-rate summary — the calc, made explicit. */}
              <div className="rounded-lg bg-fill-soft border border-line px-3 py-2 space-y-1">
                {blended.crew > 0 && blended.man_hours > 0 ? (
                  <div className="text-[11px] text-ink-faint">
                    {blended.crew} crew · {blended.man_hours} man-hours · blended{' '}
                    <span className="font-medium text-ink-dim">
                      {formatMoneyCents(blended.blended_rate_cents)}/hr
                    </span>
                  </div>
                ) : (
                  <div className="text-[11px] text-ink-faint">
                    Add crew and hours to calculate labor.
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Labor</span>
                  <span className="text-sm font-semibold font-mono">
                    {formatMoneyCents(blended.labor_cents)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-ink-faint text-sm">$</span>
              <Input
                type="number"
                min={0}
                step={5}
                value={flatStr}
                onChange={(e) => setFlat(e.target.value)}
                className="h-9 w-40"
                placeholder="0.00"
              />
              <span className="text-[11px] text-ink-faint">flat labor (no role breakdown)</span>
            </div>
          )}
        </div>

        {/* Right column — round-trip miles (top) + debris removal (bottom) */}
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-ink-faint">Round-trip miles</label>
            {value.mileage_legs ? (
              <>
                <div className="flex items-center gap-1.5 mt-1">
                  <div className="h-9 flex-1 rounded-md border border-line bg-fill-soft px-2 flex items-center text-sm tabular-nums">
                    {value.mileage_miles ?? 0} mi
                  </div>
                  {routing && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCalcOpen(true)}
                    >
                      Edit
                    </Button>
                  )}
                  <button
                    type="button"
                    aria-label="Detach journey — enter miles manually"
                    title="Detach journey — enter miles manually"
                    onClick={() => onChange({ ...value, mileage_legs: null })}
                    className="w-8 h-8 grid place-items-center rounded-md border border-line text-ink-faint hover:bg-fill-soft shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-[11px] text-ink-faint mt-1">
                  {value.mileage_legs.legs.length} legs · {formatMoneyCents(preview.mileage_cents)}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5 mt-1">
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={milesStr}
                    onChange={(e) => setMiles(e.target.value)}
                    className="h-9"
                    placeholder="0"
                  />
                  {routing && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setCalcOpen(true)}
                    >
                      <MapIcon className="w-3.5 h-3.5" /> Calculate
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-ink-faint mt-1">
                  × {formatMoneyCents(s.mileage_per_mile_cents)}/mi ={' '}
                  {formatMoneyCents(preview.mileage_cents)}
                </p>
              </>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-ink-faint">Debris removal</label>
            <div
              className="mt-1"
              title="Click a section of the box to set the debris load; click the filled level again to clear it."
            >
              <DebrisTruck
                quarters={quarters}
                perQuarterCents={s.debris_per_quarter_cents}
                onSelect={setQuarters}
              />
            </div>
            <p className="text-[11px] text-ink-faint mt-1">
              {TIERS.find((t) => t.q === quarters)?.label ?? 'None'} ·{' '}
              {formatMoneyCents(preview.debris_cents)}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-line pt-3">
        <span className="text-sm font-medium">Total bid</span>
        <span className="text-lg font-semibold font-mono">
          {formatMoneyCents(preview.total_cents)}
        </span>
      </div>

      {routing && calcOpen && (
        <MileageCalculatorModal
          open={calcOpen}
          onClose={() => setCalcOpen(false)}
          routing={routing}
          initial={value.mileage_legs}
          ratePerMileCents={s.mileage_per_mile_cents}
          onConfirm={(miles, itinerary) => {
            onChange({ ...value, mileage_miles: miles, mileage_legs: itinerary });
            setCalcOpen(false);
          }}
        />
      )}
    </div>
  );
}
